import { queryOptions } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { publicPath } from "@/lib/branch";
import { availabilitySchema } from "../schemas/availability.schemas";

export interface AvailabilityParams {
  serviceIds: readonly string[];
  /** The first date, `yyyy-mm-dd` in the salon's zone. */
  from: string;
  /** How many dates to answer for; the server caps it. */
  days: number;
  /** A preferred stylist for every service. Ignored when the salon does not offer the choice. */
  staffId?: string | null;
  /**
   * The customer's own visit being moved. The customer's route is asked instead of the public one:
   * the visit's own time is not counted as busy and its stylists are kept where they can take it.
   * The services are the visit's, so `serviceIds` is not sent.
   */
  rescheduleOf?: string | null;
}

export const availabilityKeys = {
  all: ["availability"] as const,
  window: (params: AvailabilityParams) =>
    [
      ...availabilityKeys.all,
      {
        serviceIds: [...params.serviceIds],
        from: params.from,
        days: params.days,
        staffId: params.staffId ?? null,
        rescheduleOf: params.rescheduleOf ?? null,
      },
    ] as const,
};

/**
 * When the customer could come in, for a window of dates (TDD-SA FR-SA-030 to FR-SA-034).
 *
 * Anonymous, like the rest of the salon's page: a customer checks times before signing in. The
 * answer reserves nothing, so it is re-read whenever it is looked at again (`staleTime` 0), and a
 * booking refused with `salon.slot_taken` refreshes it.
 */
export function availabilityQuery(params: AvailabilityParams) {
  // The client's query option takes single values; the service list repeats the key, as ASP.NET binds it.
  const services = params.serviceIds.map((id) => `serviceIds=${encodeURIComponent(id)}`).join("&");
  return queryOptions({
    queryKey: availabilityKeys.window(params),
    queryFn: ({ signal }) =>
      params.rescheduleOf
        ? apiClient.get(
            availabilitySchema,
            `/api/v1/salon/me/appointments/${encodeURIComponent(params.rescheduleOf)}/availability`,
            { query: { from: params.from, days: params.days }, signal },
          )
        : apiClient.get(availabilitySchema, `${publicPath("/availability")}?${services}`, {
            query: { from: params.from, days: params.days, staffId: params.staffId ?? undefined },
            signal,
          }),
    enabled: params.serviceIds.length > 0 && params.from !== "",
    staleTime: 0,
  });
}
